import L from 'leaflet';
import type {} from '@geoman-io/leaflet-geoman-free';
import { PolygonFilter } from '../../models/polygon-filter.model';

export function polygonCoordinates(points: readonly L.LatLng[]): PolygonFilter {
  const result = points.map((p) => ({ latitude: p.lat, longitude: p.lng }));
  if (
    result.length > 1 &&
    result[0].latitude === result[result.length - 1].latitude &&
    result[0].longitude === result[result.length - 1].longitude
  )
    result.pop();
  return result;
}

/** Owns one search layer. Geoman never receives a store object or triggers a search. */
export class PolygonDrawingAdapter {
  static async create(map: L.Map, onChange: () => void): Promise<PolygonDrawingAdapter> {
    await import('@geoman-io/leaflet-geoman-free');
    // Geoman was loaded after this map was created. Its runtime supports maps,
    // although the published reInitLayer signature only declares Layer.
    if (!map.pm) L.PM.reInitLayer(map as unknown as L.Layer);
    if (!map.pm || typeof map.pm.enableDraw !== 'function') throw new Error('POLYGON_DRAW_INIT_FAILED');
    return new PolygonDrawingAdapter(map, onChange);
  }
  private layer?: L.Polygon;
  private working?: L.Polyline;
  private committed: PolygonFilter | null = null;
  private doubleClickZoom = false;
  mode: 'idle' | 'drawing' | 'editing' = 'idle';
  private readonly created = (event: L.LeafletEvent) => {
    const { layer, shape } = event as L.LeafletEvent & { layer: L.Polygon; shape: string };
    if (this.mode !== 'drawing' || shape !== 'Polygon') return;
    this.layer = layer;
    this.stopDrawing();
    this.enableEditing();
  };
  private readonly started = (event: L.LeafletEvent) => {
    this.detachWorking();
    this.working = (event as L.LeafletEvent & { workingLayer: L.Polyline }).workingLayer;
    this.working.on('pm:change pm:vertexadded pm:vertexremoved', this.changed);
  };
  private readonly changed = () => this.onChange();

  constructor(
    private map: L.Map,
    private onChange: () => void
  ) {
    this.map.on('pm:create', this.created);
    this.map.on('pm:drawstart', this.started);
  }

  load(polygon: PolygonFilter | null, fit = false): void {
    this.committed = polygon ? polygon.map((p) => ({ ...p })) : null;
    this.cancel();
    if (fit && this.layer)
      this.map.fitBounds(this.layer.getBounds(), { padding: [30, 30], maxZoom: 15 });
  }
  draw(): void {
    this.removeLayer();
    this.doubleClickZoom = this.map.doubleClickZoom.enabled();
    this.map.doubleClickZoom.disable();
    this.mode = 'drawing';
    this.map.pm.enableDraw('Polygon', {
      snappable: false,
      allowSelfIntersection: false,
      tooltips: false,
      continueDrawing: false,
      pathOptions: { pmIgnore: false, snapIgnore: true },
      finishOnEnter: true,
    });
    this.onChange();
  }
  finish(): void {
    const points = this.working?.getLatLngs() as L.LatLng[] | undefined;
    if (!points) throw new Error('POLYGON_TOO_FEW_VERTICES');
    const polygon = polygonCoordinates(points);
    this.validate(polygon);
    this.stopDrawing();
    this.layer = this.createLayer(polygon);
    this.enableEditing();
  }
  edit(): void {
    if (this.layer) this.enableEditing();
  }
  read(): PolygonFilter {
    const polygon = polygonCoordinates((this.layer?.getLatLngs()[0] ?? []) as L.LatLng[]);
    this.validate(polygon);
    return polygon;
  }
  apply(): PolygonFilter {
    const polygon = this.read();
    this.load(polygon);
    return polygon;
  }
  cancel(): void {
    this.stopDrawing();
    this.removeLayer();
    if (this.committed) this.layer = this.createLayer(this.committed);
    this.mode = 'idle';
    this.onChange();
  }
  clear(): void {
    this.load(null);
  }
  destroy(): void {
    this.stopDrawing();
    this.removeLayer();
    this.map.off('pm:create', this.created);
    this.map.off('pm:drawstart', this.started);
  }
  private validate(polygon: PolygonFilter): void {
    if (new Set(polygon.map((p) => p.latitude + ',' + p.longitude)).size < 3)
      throw new Error('POLYGON_TOO_FEW_VERTICES');
    if (polygon.length > 100) throw new Error('POLYGON_TOO_MANY_VERTICES');
    if (
      polygon.some(
        (p) =>
          !Number.isFinite(p.latitude) ||
          !Number.isFinite(p.longitude) ||
          Math.abs(p.latitude) >= 90 ||
          Math.abs(p.longitude) > 180
      )
    )
      throw new Error('POLYGON_COORDINATE_INVALID');
  }
  private createLayer(polygon: PolygonFilter): L.Polygon {
    return L.polygon(
      polygon.map((p) => [p.latitude, p.longitude] as L.LatLngTuple),
      { color: '#1976d2', fillOpacity: 0.12, pmIgnore: false, snapIgnore: true }
    ).addTo(this.map);
  }
  private enableEditing(): void {
    this.mode = 'editing';
    this.layer?.pm.enable({ snappable: false, allowSelfIntersection: false });
    this.layer?.on('pm:edit pm:change', this.changed);
    this.onChange();
  }
  private detachWorking(): void {
    this.working?.off('pm:change pm:vertexadded pm:vertexremoved', this.changed);
    this.working = undefined;
  }
  private stopDrawing(): void {
    this.detachWorking();
    if (this.mode === 'drawing') {
      this.map.pm.disableDraw();
      if (this.doubleClickZoom) this.map.doubleClickZoom.enable();
    }
  }
  private removeLayer(): void {
    this.layer?.off('pm:edit pm:change', this.changed);
    this.layer?.pm.disable();
    this.layer?.remove();
    this.layer = undefined;
  }
}
